import React, { useState } from "react";
import './Calculator.css';
import { Heading } from "../../components";
import { FaLightbulb, FaMoneyBillWave, FaHome, FaSun, FaRuler, FaTree, FaBatteryThreeQuarters } from "react-icons/fa";

const SolarCalculator = () => {

  // State for user inputs
  const [electricityUsage, setElectricityUsage] = useState("");
  const [electricityRate, setElectricityRate] = useState("");
  const [roofType, setRoofType] = useState("");
  const [roofDirection, setRoofDirection] = useState("");
  const [roofTilt, setRoofTilt] = useState("");
  const [roofArea, setRoofArea] = useState("");
  const [shadeCoverage, setShadeCoverage] = useState("");
  const [batteryStorage, setBatteryStorage] = useState("no");
  const [location, setLocation] = useState("");

  // State for calculated results
  const [systemSize, setSystemSize] = useState(0);
  const [panelCount, setPanelCount] = useState(0);
  const [estimatedCost, setEstimatedCost] = useState(0);
  const [monthlySavings, setMonthlySavings] = useState(0);
  const [afterIncentivesCost, setAfterIncentivesCost] = useState(0);
  const [roofSpaceMessage, setRoofSpaceMessage] = useState("");
  const [annualSavings, setAnnualSavings] = useState(0);
  const [paybackPeriod, setPaybackPeriod] = useState(0);
  const [dailyProduction, setDailyProduction] = useState(0);

  // Constants for calculations
  const PANEL_WATTAGE = 400; // Typical panel size in watts (modern panels)
  const PANEL_SIZE_SQFT = 21.13; // Average panel size in square feet (65" x 39" typical dimensions)
  const COST_PER_WATT = 2.75; // Average installation cost per watt (varies by region)
  const FEDERAL_TAX_CREDIT = 0.30; // 30% federal tax credit
  const BATTERY_COST = 10000; // Additional cost for battery storage (varies by size)
  const AVERAGE_SUN_HOURS = {
    "north": 3.5,    // Northern states average
    "south": 5.5,    // Southern states average
    "east": 4.5,     // Eastern states average
    "west": 5.0,     // Western states average
    "default": 4.5   // National average
  };
  const PANEL_EFFICIENCY = 0.20; // 20% efficiency for modern panels
  const SYSTEM_LOSSES = 0.14; // 14% system losses (inverter, wiring, etc.)

  // Calculate solar system details
  const calculateSolar = () => {
    // Reset messages
    setRoofSpaceMessage("");

    // Basic validation
    if (!electricityUsage || !electricityRate) {
      alert("Please enter your electricity usage and rate");
      return;
    }

    // Convert inputs to numbers
    const usage = parseFloat(electricityUsage);
    const rate = parseFloat(electricityRate);
    const area = roofArea ? parseFloat(roofArea) : null;
    const tilt = roofTilt ? parseFloat(roofTilt) : 30; // Default to 30 degrees if not specified

    // Validate inputs
    if (usage <= 0 || rate <= 0) {
      alert("Please enter positive values for electricity usage and rate");
      return;
    }

    // Get base sun hours based on location/direction
    const baseSunHours = AVERAGE_SUN_HOURS[roofDirection] || AVERAGE_SUN_HOURS.default;
    
    // Calculate production factor adjustments
    const productionFactor = getProductionFactor(roofDirection, tilt, shadeCoverage);
    
    // Calculate daily electricity needed (kWh)
    const dailyUsage = usage / 30.4; // Average days per month
    
    // Calculate required system size (kW)
    const systemEfficiency = PANEL_EFFICIENCY * (1 - SYSTEM_LOSSES);
    const calculatedSystemSize = dailyUsage / (baseSunHours * productionFactor * systemEfficiency);
    const roundedSystemSize = parseFloat(calculatedSystemSize.toFixed(1));
    
    // Calculate number of panels needed
    const requiredPanels = Math.ceil((roundedSystemSize * 1000) / PANEL_WATTAGE);
    
    // Check roof space constraints if area is provided
    let actualPanels = requiredPanels;
    if (area) {
      const maxPanelsByArea = Math.floor(area / PANEL_SIZE_SQFT);
      
      if (requiredPanels > maxPanelsByArea) {
        setRoofSpaceMessage(`Your roof can only fit ${maxPanelsByArea} panels (${(maxPanelsByArea * PANEL_WATTAGE / 1000).toFixed(1)} kW)`);
        actualPanels = maxPanelsByArea;
      }
    }
    
    // Recalculate actual system size based on panel count
    const actualSystemSize = (actualPanels * PANEL_WATTAGE) / 1000;
    
    // Calculate energy production
    const calculatedDailyProduction = actualSystemSize * baseSunHours * productionFactor * systemEfficiency;
    
    // Calculate costs
    let baseCost = actualSystemSize * 1000 * COST_PER_WATT;
    if (batteryStorage === "yes") {
      baseCost += BATTERY_COST;
    }
    
    // Calculate savings
    const monthlyProduction = calculatedDailyProduction * 30.4;
    const monthlyValue = monthlyProduction * rate;
    const annualValue = monthlyValue * 12;
    
    // Calculate payback period (years)
    const netCost = baseCost * (1 - FEDERAL_TAX_CREDIT);
    const simplePayback = netCost / annualValue;
    
    // Update state with calculated values
    setSystemSize(actualSystemSize.toFixed(1));
    setPanelCount(actualPanels);
    setEstimatedCost(baseCost.toLocaleString());
    setAfterIncentivesCost(netCost.toLocaleString());
    setMonthlySavings(monthlyValue.toFixed(2));
    setAnnualSavings(annualValue.toFixed(2));
    setPaybackPeriod(simplePayback.toFixed(1));
    setDailyProduction(calculatedDailyProduction.toFixed(1));
  };

  // Helper function to adjust production factor based on roof characteristics
  const getProductionFactor = (direction, tilt, shade) => {
    let factor = 1.0; // Base production factor
    
    // Adjust for roof direction (optimal is south in northern hemisphere)
    if (direction === "south") factor *= 1.0;
    else if (direction === "east" || direction === "west") factor *= 0.85;
    else if (direction === "north") factor *= 0.70;
    
    // Adjust for roof tilt (optimal is latitude -15° in winter, latitude +15° in summer)
    // We'll use a simplified adjustment
    const optimalTilt = 30; // Good average for most locations
    const tiltDifference = Math.abs(tilt - optimalTilt);
    if (tiltDifference > 30) factor *= 0.85;
    else if (tiltDifference > 15) factor *= 0.92;
    
    // Adjust for shade
    if (shade === "partial") factor *= 0.80; // 20% reduction
    else if (shade === "heavy") factor *= 0.50; // 50% reduction
    
    return factor;
  };

  return (
    <section className="custom-form solar-calculator">
      <div className="box-container">
        <div className="content">
          <form className="calculator-form dark-form">
            <Heading title={'Solar Calculator'} subTitle={'Estimate Your Solar Savings'} sideHeading linear />

            {/* Electricity Usage */}
            <div className="input-box">
              <div className="input-field">
                <label htmlFor="electricity-usage"><FaLightbulb /></label>
                <input
                  type="number"
                  id="electricity-usage"
                  className="box"
                  placeholder="Monthly Electricity Usage (kWh)"
                  value={electricityUsage}
                  onChange={(e) => setElectricityUsage(e.target.value)}
                  min="1"
                  required
                />
              </div>
              <div className="input-field">
                <label htmlFor="electricity-rate"><FaMoneyBillWave /></label>
                <input
                  type="number"
                  id="electricity-rate"
                  className="box"
                  placeholder="Electricity Rate ($/kWh)"
                  value={electricityRate}
                  onChange={(e) => setElectricityRate(e.target.value)}
                  min="0.01"
                  step="0.01"
                  required
                />
              </div>
            </div>

            {/* Location and Roof Details */}
            <div className="input-box">
              <div className="input-field select-field">
                <label htmlFor="location"><FaSun /></label>
                <select
                  id="location"
                  className="box"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                >
                  <option value="">Select Region</option>
                  <option value="north">Northern States</option>
                  <option value="south">Southern States</option>
                  <option value="east">Eastern States</option>
                  <option value="west">Western States</option>
                </select>
              </div>
              <div className="input-field select-field">
                <label htmlFor="roof-type"><FaHome /></label>
                <select
                  id="roof-type"
                  className="box"
                  value={roofType}
                  onChange={(e) => setRoofType(e.target.value)}
                >
                  <option value="">Roof Type</option>
                  <option value="asphalt">Asphalt Shingle</option>
                  <option value="metal">Metal</option>
                  <option value="tile">Tile</option>
                  <option value="flat">Flat</option>
                </select>
              </div>
            </div>

            <div className="input-box">
              <div className="input-field select-field">
                <label htmlFor="roof-direction"><FaSun /></label>
                <select
                  id="roof-direction"
                  className="box"
                  value={roofDirection}
                  onChange={(e) => setRoofDirection(e.target.value)}
                >
                  <option value="">Roof Direction</option>
                  <option value="south">South</option>
                  <option value="east">East</option>
                  <option value="west">West</option>
                  <option value="north">North</option>
                </select>
              </div>
              <div className="input-field">
                <label htmlFor="roof-tilt"><FaSun /></label>
                <input
                  type="number"
                  id="roof-tilt"
                  className="box"
                  placeholder="Roof Tilt (degrees)"
                  value={roofTilt}
                  onChange={(e) => setRoofTilt(e.target.value)}
                  min="0"
                  max="90"
                />
              </div>
            </div>

            <div className="input-box">
              <div className="input-field">
                <label htmlFor="roof-area"><FaRuler /></label>
                <input
                  type="number"
                  id="roof-area"
                  className="box"
                  placeholder="Available Roof Area (sq. ft.)"
                  value={roofArea}
                  onChange={(e) => setRoofArea(e.target.value)}
                  min="1"
                />
              </div>
              <div className="input-field select-field">
                <label htmlFor="shade-coverage"><FaTree /></label>
                <select
                  id="shade-coverage"
                  className="box"
                  value={shadeCoverage}
                  onChange={(e) => setShadeCoverage(e.target.value)}
                >
                  <option value="">Shade Coverage</option>
                  <option value="full">Full Sun</option>
                  <option value="partial">Partial Shade</option>
                  <option value="heavy">Heavy Shade</option>
                </select>
              </div>
            </div>

            {/* Battery Storage */}
            <div className="input-box">
              <div className="input-field select-field">
                <label htmlFor="battery-storage"><FaBatteryThreeQuarters /></label>
                <select
                  id="battery-storage"
                  className="box"
                  value={batteryStorage}
                  onChange={(e) => setBatteryStorage(e.target.value)}
                >
                  <option value="no">No Battery Storage</option>
                  <option value="yes">Include Battery Storage</option>
                </select>
              </div>
            </div>

            {/* Calculate Button */}
            <div className="form-group">
              <button
                type="button"
                className="btn"
                onClick={calculateSolar}
              >
                Calculate Solar Savings
              </button>
            </div>

            {/* Results Section */}
            {systemSize > 0 && (
              <div className="results-container">
                <h4>Your Solar Estimate</h4>
                {roofSpaceMessage && (
                  <div className="warning-message">
                    <p>{roofSpaceMessage}</p>
                  </div>
                )}
                
                <div className="results-grid">
                  <div className="result-item">
                    <h5>System Size</h5>
                    <span>{systemSize} kW</span>
                  </div>
                  <div className="result-item">
                    <h5>Panels Needed</h5>
                    <span>{panelCount}</span>
                  </div>
                </div>
                
                <div className="results-grid">
                  <div className="result-item">
                    <h5>Estimated Cost</h5>
                    <span>${estimatedCost}</span>
                  </div>
                  <div className="result-item">
                    <h5>After Incentives</h5>
                    <span>${afterIncentivesCost}</span>
                  </div>
                </div>
                
                <div className="results-grid">
                  <div className="result-item">
                    <h5>Daily Production</h5>
                    <span>{dailyProduction} kWh</span>
                  </div>
                  <div className="result-item">
                    <h5>Monthly Savings</h5>
                    <span>${monthlySavings}</span>
                  </div>
                </div>
                
                <div className="results-grid">
                  <div className="result-item">
                    <h5>Annual Savings</h5>
                    <span>${annualSavings}</span>
                  </div>
                  <div className="result-item">
                    <h5>Payback Period</h5>
                    <span>{paybackPeriod} years</span>
                  </div>
                </div>
                
                <div className="results-grid">
                  <div className="result-item">
                    <h5>Required Roof Space</h5>
                    <span>{(panelCount * PANEL_SIZE_SQFT).toFixed(1)} sq. ft.</span>
                  </div>
                  <div className="result-item">
                    <h5>Space Available</h5>
                    <span>{roofArea || "Not specified"} sq. ft.</span>
                  </div>
                </div>
                
                {batteryStorage === "yes" && (
                  <div className="warning-message">
                    <p>Note: Battery storage adds ${BATTERY_COST.toLocaleString()} to the system cost.</p>
                  </div>
                )}
                
                <div className="warning-message">
                  <p>Note: These estimates are for informational purposes only. Actual system performance may vary based on local conditions, installation quality, and other factors.</p>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
};

export default SolarCalculator;